/**
 * Teen Patti 20-20 — the rules, pure. Plan §6.3 (docs/ADRENALINE_SHARED_TABLES_PLAN.md).
 *
 * Two 3-card hands, PLAYER A vs PLAYER B; players bet on a side at X1.98
 * (total return). Ranking: Trail > Pure Sequence > Sequence > Colour > Pair >
 * High card; A-K-Q is the top sequence, A-2-3 the second, then K-Q-J … 4-3-2.
 * Suits never rank; identical ranks = PUSH (stake returned as a win of X1.00).
 *
 * Candidates for the engine = N complete 6-card deals drawn by CSPRNG at lock
 * (π = 1/N); nothing is sampled to fit a class.
 */
import { type Card, type Rank, freshDeck, shuffle, rankValue } from '../_cards/deck';

export type Side = 'a' | 'b';
export const PAYOUT = 1.98;                       // total return on the winning side
export const PUSH_X = 1.0;
export const N_DEALS = 96;

export type Category = 6 | 5 | 4 | 3 | 2 | 1;     // 6 = trail … 1 = high card
export const CATEGORY_NAME: Record<Category, string> = {
  6: 'TRAIL', 5: 'PURE SEQUENCE', 4: 'SEQUENCE', 3: 'COLOUR', 2: 'PAIR', 1: 'HIGH CARD',
};

export type HandRank = { cat: Category; key: number[]; name: string };

/** A=14 for ranking purposes */
const hi = (r: Rank) => (r === 'A' ? 14 : rankValue(r));

/** sequence strength: A-K-Q = 14, A-2-3 = 13.5 (second), K-Q-J = 13 … 4-3-2 = 4 */
function seqStrength(vals: number[]): number | null {
  const v = [...vals].sort((a, b) => b - a);            // desc
  if (v[0] === 14 && v[1] === 13 && v[2] === 12) return 14;
  if (v[0] === 14 && v[1] === 3 && v[2] === 2) return 13.5;
  if (v[0] - v[1] === 1 && v[1] - v[2] === 1) return v[0];
  return null;
}

export function rankHand(cards: Card[]): HandRank {
  if (cards.length !== 3) throw new Error('three cards');
  const vals = cards.map((c) => hi(c.rank)).sort((a, b) => b - a);
  const flush = cards.every((c) => c.suit === cards[0].suit);
  const seq = seqStrength(vals);
  if (vals[0] === vals[1] && vals[1] === vals[2]) return { cat: 6, key: [vals[0]], name: CATEGORY_NAME[6] };
  if (seq != null && flush) return { cat: 5, key: [seq], name: CATEGORY_NAME[5] };
  if (seq != null) return { cat: 4, key: [seq], name: CATEGORY_NAME[4] };
  if (flush) return { cat: 3, key: vals, name: CATEGORY_NAME[3] };
  if (vals[0] === vals[1]) return { cat: 2, key: [vals[0], vals[2]], name: CATEGORY_NAME[2] };
  if (vals[1] === vals[2]) return { cat: 2, key: [vals[1], vals[0]], name: CATEGORY_NAME[2] };
  return { cat: 1, key: vals, name: CATEGORY_NAME[1] };
}

/** >0 if a beats b, <0 if b beats a, 0 = push */
export function compare(a: HandRank, b: HandRank): number {
  if (a.cat !== b.cat) return a.cat - b.cat;
  for (let i = 0; i < Math.max(a.key.length, b.key.length); i++) {
    const d = (a.key[i] ?? 0) - (b.key[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

export type Deal = { a: Card[]; b: Card[]; winner: Side | 'push'; ra: HandRank; rb: HandRank };

export function deal(rng: () => number): Deal {
  const d = shuffle(freshDeck(), rng);
  /* dealt alternately A,B,A,B,A,B like a real table */
  const a = [d[0], d[2], d[4]], b = [d[1], d[3], d[5]];
  const ra = rankHand(a), rb = rankHand(b);
  const c = compare(ra, rb);
  return { a, b, ra, rb, winner: c > 0 ? 'a' : c < 0 ? 'b' : 'push' };
}

/** the candidate set the scorer picks from. PHASE-2 SEAM: server-side. */
export function candidates(rng: () => number, n = N_DEALS): Deal[] {
  return Array.from({ length: n }, () => deal(rng));
}

/** what a bet returns for a given outcome (X-doctrine total return) */
export function betReturn(stake: number, side: Side, winner: Side | 'push'): number {
  if (winner === 'push') return Math.floor(stake * PUSH_X);
  return winner === side ? Math.floor(stake * PAYOUT + 1e-9) : 0;
}

export const fmtX = (x: number) => `X${(Math.floor(x * 100 + 1e-9) / 100).toFixed(2)}`;
